import { c as _c } from "react/compiler-runtime";
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { useIsVisible } from '@sbt-web/hooks';
import { OptimizelySubitoContext } from '@sbt-web/houston-wrapper';
import { getUserFeedbacks } from '@/api/getUserFeedbacks';
import { getNormalizedScore, UserRating } from '@/client/components/Detail/UserRating';
import useUser from '@/client/contexts/UserContext';
import { viewReviewModalAction } from '@/tools/tracking/ProfileTracking';
import type { Feedback, FeedbackItem, ReviewFlow, UserFeedback, UserFeedbackResponse } from '@/types';
import { isProfileOwner } from '@/utils/profile';
import { TRUST_DEMERGE_ROLLOUT_TOGGLE } from '@/shared/constants';
import { TextualReview } from '../TextualReview';
import styles from './index.module.scss';
interface TextualReviewModalContentProps {
  userName: string;
  ownerId: string;
  score: number;
  reviewCount: number;
}
type ReviewHeadProps = TextualReviewModalContentProps;
type ReviewCardProps = TextualReviewModalContentProps & {
  date: Feedback['givenAt'];
  flow: ReviewFlow;
  isAutomaticReview: Feedback['isAutomaticReview'];
  reviewerRole: UserFeedback['role'];
  reviewerName: UserFeedback['name'];
  text: Feedback['textReview'];
  itemTitle: FeedbackItem['title'];
  reviewerProfileUrl?: string;
};
const PAGE_LIMIT = 30;
const INITIAL_PAGE_NUMBER = 0;
export const getReviewerProfileUrl = (reviewerId: string | null, isAutomaticReview: boolean): string | undefined => {
  if (isAutomaticReview || !reviewerId) {
    return undefined;
  }
  const ownerId = reviewerId.split(':').pop();
  if (ownerId && /^\d+$/.test(ownerId)) {
    return `${process.env.NEXT_PUBLIC_ENV_BASE_URL}/utente/${ownerId}`;
  }
  return undefined;
};
const ReviewHead = t0 => {
  const $ = _c(26);
  const {
    score,
    reviewCount,
    ownerId,
    userName
  } = t0;
  const {
    user
  } = useUser();
  let t1;
  if ($[0] !== score) {
    t1 = getNormalizedScore(score);
    $[0] = score;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const normalizedScore = t1;
  const isOwner = isProfileOwner(ownerId, user?.id);
  const t2 = isOwner ? "te" : userName;
  let t3;
  if ($[2] !== t2) {
    t3 = <h4 className="headline-4">Dicono di {t2}</h4>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== reviewCount) {
    t4 = reviewCount === 1 && <p className={classNames("body-text small", [styles.few_review_text])}>Anche se ci sono poche recensioni su questo utente, concludi un affare sicuro grazie alla nostra <a target="_blank" href="https://assistenza.subito.it/hc/it/articles/4403733868945-Protezione-Acquisti?utm_medium=autopromo&utm_source=subito&utm_campaign=free-attraction-pmm_education_user_profile_buyer&utm_content=autopromo_pmm_widget_tuttosubito_user_profile_page_mar2025" rel="noreferrer" className={styles["protection-link"]}>protezione acquisti</a>{""}.</p>;
    $[4] = reviewCount;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = classNames("headline-6", [styles.rounded_score]);
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== normalizedScore) {
    t6 = <h6 className={t5}>{normalizedScore}</h6>;
    $[7] = normalizedScore;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== ownerId || $[10] !== reviewCount || $[11] !== score) {
    t7 = <UserRating score={score} reviewCount={reviewCount} userId={ownerId} showScore={false} />;
    $[9] = ownerId;
    $[10] = reviewCount;
    $[11] = score;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = classNames("body-text small", classNames([styles.review_count_text]));
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  const t9 = `${reviewCount} ${reviewCount === 1 ? "recensione" : "recensioni"}`;
  let t10;
  if ($[14] !== t9) {
    t10 = <p className={t8}>{t9}</p>;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10 || $[17] !== t7) {
    t11 = <div className={styles.right_box}>{t7}{t10}</div>;
    $[16] = t10;
    $[17] = t7;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  let t12;
  if ($[19] !== t11 || $[20] !== t6) {
    t12 = <section className={styles.review_head}>{t6}{t11}</section>;
    $[19] = t11;
    $[20] = t6;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== t12 || $[23] !== t3 || $[24] !== t4) {
    t13 = <div className={styles.heading}>{t3}{t4}{t12}</div>;
    $[22] = t12;
    $[23] = t3;
    $[24] = t4;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  return t13;
};
const ReviewCard = t0 => {
  const $ = _c(15);
  const {
    reviewCount,
    score,
    ownerId,
    userName,
    date,
    isAutomaticReview,
    text,
    itemTitle,
    reviewerRole,
    reviewerName,
    flow,
    reviewerProfileUrl
  } = t0;
  let t1;
  if ($[0] !== date) {
    t1 = new Date(date).toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
    $[0] = date;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const formattedDate = t1;
  const t2 = text ?? undefined;
  const t3 = itemTitle ?? undefined;
  let t4;
  if ($[2] !== flow || $[3] !== formattedDate || $[4] !== isAutomaticReview || $[5] !== ownerId || $[6] !== reviewCount || $[7] !== reviewerName || $[8] !== reviewerProfileUrl || $[9] !== reviewerRole || $[10] !== score || $[11] !== t2 || $[12] !== t3 || $[13] !== userName) {
    t4 = <li className={styles.feedback_card}><TextualReview userId={ownerId} score={score} reviewCount={reviewCount} date={formattedDate} flow={flow} isAutomaticReview={isAutomaticReview} text={t2} itemTitle={t3} reviewerRole={reviewerRole} reviewerName={reviewerName} userName={userName} reviewerProfileUrl={reviewerProfileUrl} /></li>;
    $[2] = flow;
    $[3] = formattedDate;
    $[4] = isAutomaticReview;
    $[5] = ownerId;
    $[6] = reviewCount;
    $[7] = reviewerName;
    $[8] = reviewerProfileUrl;
    $[9] = reviewerRole;
    $[10] = score;
    $[11] = t2;
    $[12] = t3;
    $[13] = userName;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
};
export const TextualReviewModalContent = ({
  userName,
  reviewCount,
  score,
  ownerId
}: TextualReviewModalContentProps) => {
  const {
    optimizely
  } = useContext(OptimizelySubitoContext);
  const [feedbacks, setFeedbacks] = useState<UserFeedbackResponse>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorState, setErrorState] = useState<boolean>(false);
  const reviewCardObserver = useRef(null);
  const [isInViewport, setElement, unobserve] = useIsVisible({
    observerOptions: {
      threshold: 1
    }
  });
  const [pageNumber, setPageNumber] = useState<number>(INITIAL_PAGE_NUMBER);
  const isFetchingRef = useRef<boolean>(false);
  const isUserWithFeedbacks = feedbacks !== undefined && feedbacks?.result.length > 0;
  const withNextPage = feedbacks !== undefined && pageNumber < feedbacks.pagination.totalPages;
  const fetchTextualReviews = useCallback(async (pageNum: number) => {
    setIsLoading(true);
    setErrorState(false);
    isFetchingRef.current = true;
    let switchToAidos = false;
    if (optimizely) {
      switchToAidos = optimizely.isFeatureEnabled(TRUST_DEMERGE_ROLLOUT_TOGGLE);
    }
    const userFeedbacks = await getUserFeedbacks(ownerId, PAGE_LIMIT, pageNum, switchToAidos);
    if (userFeedbacks?.pagination && userFeedbacks?.result) {
      setFeedbacks(prevUserFeedbacks => {
        if (prevUserFeedbacks === undefined) {
          //first call: show the first page
          return userFeedbacks;
        }

        //show the next pages
        return {
          ...prevUserFeedbacks,
          result: [...prevUserFeedbacks.result, ...userFeedbacks.result],
          pagination: {
            ...prevUserFeedbacks.pagination,
            ...userFeedbacks.pagination
          }
        };
      });
      setPageNumber(pageNum + 1);
    } else {
      setErrorState(true);
    }
    isFetchingRef.current = false;
    setIsLoading(false);
  }, [ownerId]);
  useEffect(() => {
    viewReviewModalAction(ownerId);
    fetchTextualReviews(INITIAL_PAGE_NUMBER);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    //avoid observing the ref element getting the reviews for the first time
    if (isUserWithFeedbacks && reviewCardObserver?.current) {
      setElement(reviewCardObserver.current);
    }
    return () => unobserve();
  }, [reviewCardObserver, setElement, isUserWithFeedbacks, unobserve]);
  useEffect(() => {
    //make the requests only when the user reviews are greater than the page limit
    if (isInViewport && isUserWithFeedbacks && withNextPage && !isFetchingRef.current) {
      fetchTextualReviews(pageNumber);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInViewport]);
  return <div className={styles.wrapper}>
      <ReviewHead score={score} userName={userName} reviewCount={reviewCount} ownerId={ownerId} />
      <ul className={styles.list} aria-label="Lista delle recensioni" role="region">
        {feedbacks?.result.map(item => <ReviewCard key={item.feedback.tradeId} date={item.feedback.givenAt} score={item.feedback.score} reviewCount={reviewCount} ownerId={ownerId} userName={userName} isAutomaticReview={item.feedback.isAutomaticReview} text={item.feedback.textReview} itemTitle={item.feedback.item.title} reviewerRole={item.role} reviewerName={item.name} flow={item.feedback.segmentExternalId === 'subito-pick-a-buyer' ? 'PAB' : 'TUTTOSUBITO'} reviewerProfileUrl={getReviewerProfileUrl(item.feedback.ownerId, item.feedback.isAutomaticReview)} />)}
        {errorState && <p className={classNames('body-text', styles.error_text)}>
            Ops! Qualcosa è andato storto, ricarica la pagina.
          </p>}
        <li aria-hidden="true" ref={reviewCardObserver}></li>
        {isLoading && <li>
            <span className={styles.loader}></span>
          </li>}
      </ul>
    </div>;
};